/**
 * DateTimePicker - Recurrence start-time adapter for CoreAI DateTimePicker
 *
 * Preserves the workflow recurrence contract while using the CoreAI native
 * datetime-local control and its constraint validation.
 */

import {
  DateTimePicker as CoreAIDateTimePicker,
  type DateTimePickerValidationResultData,
} from '@coreai/fluentui-react';
import { useCallback, useMemo, useState } from 'react';

export interface DateTimePickerProps {
  /** Called when a valid date/time is selected */
  onSelect: (selectedTime: string) => void;
  /** Called to set or clear validation errors */
  setError: (error: string | undefined) => void;
  /** Optional initial value as ISO string */
  initialValue?: string | undefined;
  /** Whether the picker is disabled */
  disabled?: boolean | undefined;
  /** Optional minimum date (defaults to today) */
  minDate?: Date | undefined;
}

/**
 * Adapts CoreAI's Date-based API to the recurrence field's ISO-string contract.
 */
export const DateTimePicker = ({
  onSelect,
  setError,
  initialValue,
  disabled = false,
  minDate: minDateProp,
}: DateTimePickerProps): JSX.Element => {
  const minDate = useMemo(() => {
    const value = new Date(minDateProp ?? Date.now());
    value.setSeconds(0, 0);
    return value;
  }, [minDateProp]);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(
    initialValue ? new Date(initialValue) : null,
  );

  const handleChange = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, data: { value: Date | null }) => {
      setSelectedDateTime(data.value);
      if (!data.value) {
        setError(undefined);
        onSelect('');
        return;
      }

      if (data.value < minDate) {
        setError('Please select a future time.');
        return;
      }

      setError(undefined);
      onSelect(data.value.toISOString());
    },
    [minDate, onSelect, setError],
  );

  const handleValidation = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, data: DateTimePickerValidationResultData) => {
      if (data.error === 'out-of-bounds') {
        setError('Please select a future time.');
      } else if (data.error) {
        setError('Please enter a valid date and time.');
      } else {
        setError(undefined);
      }
    },
    [setError],
  );

  return (
    <CoreAIDateTimePicker
      type="datetime-local"
      value={selectedDateTime}
      min={minDate}
      onChange={handleChange}
      onValidationResult={handleValidation}
      disabled={disabled}
      aria-label="Date and time"
    />
  );
};

DateTimePicker.displayName = 'DateTimePicker';
